import React, { useEffect, useState, useRef } from "react";
import { SaveOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { selectIsLoading } from "../reducers";
import {
	postForm,
	selectForm,
	selectFormStatus,
	selectQnaIsLocked,
	validate,
} from "../reducers/formSlice";
import Page from "./Page";
import { PrevButton, NextButton, HomeButton } from "./Paginator.jsx";
import { Row, Col, Spin, Button, Space } from "antd";
import CapexFormHeader from "../features/capex/FormHeader";
import RocFormHeader from "../features/roc/FormHeader";
import Steps from "./Steps";
import ResultModal from "./ResultModal";
import { Content, Footer, Header, Layout } from "./Layout";
import CapexApproval from "../features/capex/CapexApproval";
import { Link } from "react-router-dom";
import KeyMembers from "../features/capex/keyMembers";
import { useNavigation, useNotification } from "../hooks/qnaHooks";
import SummaryRenderer from "features/capex/CapexSummaryRenderer";
import { selectGlobal } from "reducers/global";
import ConfidentialityStatement from "features/roc/ConfidentialityStatement";

/*
in approval mode, you can't navigate pages
-disables next button
-disables header, because there is only 1 page
*/

const Renderer = React.forwardRef(
	({ handlePrint, handlePrintPreview }, containerRef) => {
		const status = useSelector(selectFormStatus);
		const isLoading = useSelector(selectIsLoading);
		const formStatus = useSelector(selectForm)?.status;
		const [overrideApprovalMode, setOverrideApprovalMode] = useState(false);
		const {
			appType,
			homePath,
			showFormHeader,
			showFooter,
			detailedValidation,
		} = useSelector(selectGlobal);

		useNotification(detailedValidation);

		const save = async ({ isSilentSave }) => {
			if (!isLocked) {
				try {
					dispatch(postForm({ isSilentSave }));
					return true;
				} catch {
					return false;
				}
			}
			return true;
		};

		const [
			decrementPage,
			incrementPage,
			,
			currentPageId,
			pageType,
			prevButtonVisible,
			nextButtonVisible,
			isPageValid,
		] = useNavigation({ save });

		const isLocked = useSelector(selectQnaIsLocked);
		const dispatch = useDispatch();

		//container ref for scrolling
		const ref = useRef();

		useEffect(() => {
			if (ref?.current) ref.current.scrollTop = 0; //IE does not support scrollTo(x,y)
		}, [ref, currentPageId]);

		const isDraft = formStatus === "Draft" || formStatus === "Declined";
		const isApprovalMode = !isDraft && !overrideApprovalMode;
		const showSteps =
			appType === "capex" ? overrideApprovalMode || isDraft : false;

		const PageRender = ({ pageType }) => {
			if (isApprovalMode) return <SummaryRenderer />;
			if (!pageType) return <Page id={currentPageId} />;
			else
				switch (pageType) {
					case "capexApproval":
						return <CapexApproval />;
					case "keyMembers":
						return <KeyMembers />;
					default:
						return (
							<p>{`Page type of ${pageType} is not yet supported!`}</p>
						);
				}
		};

		return (
			<div
				ref={containerRef}
				id="qna-container-print-id"
				style={{ height: "100%" }}
				// className="printing"
			>
				<Layout>
					<Header showCmeFormHeader={false} />
					{showFormHeader &&
						(appType === "capex" ? (
							<CapexFormHeader
								save={save}
								setOverrideApprovalMode={
									setOverrideApprovalMode
								}
								isApprovalMode={isApprovalMode}
								isDraft={isDraft}
							/>
						) : (
							<RocFormHeader />
						))}
					<Content
						style={appType === "roc" ? { paddingBottom: 70 } : null}
					>
						<Spin tip="Please wait..." spinning={isLoading}>
							<ResultModal />
							<Row>
								{showSteps && (
									<Col span={24}>
										<Steps currentPageId={currentPageId} />
									</Col>
								)}
								<Col span={24} ref={ref}>
									<PageRender pageType={pageType} />
								</Col>
							</Row>
						</Spin>
					</Content>
					{!isLoading && showFooter && (
						<Footer>
							<div>
								<Link to={homePath}>
									<HomeButton
										visible
										disabled={status === "loading"}
									/>
								</Link>
								{!isApprovalMode && (
									<PrevButton
										decrementPage={decrementPage}
										visible={prevButtonVisible}
										disabled={status === "loading"}
									/>
								)}
							</div>
							{!isApprovalMode && (
								<NextButton
									incrementPage={incrementPage}
									visible={nextButtonVisible}
									disabled={status === "loading"}
								/>
							)}
						</Footer>
					)}
					{appType === "roc" && (
						<>
							<Footer
								style={{
									position: "fixed",
									bottom: 0,
									left: 0,
									zIndex: 1,
									width: "100%",
									height: 75,
								}}
							>
								<div
									style={{
										display: "flex",
										width: "100%",
										justifyContent: "flex-end",
									}}
								>
									<ConfidentialityStatement />

									<Space>
										<Button
											type="primary"
											size="large"
											onClick={() => {
												handlePrintPreview();
											}}
										>
											Download a Copy
										</Button>

										<Button
											type="primary"
											icon={<SaveOutlined />}
											size="large"
											onClick={() => {
												if (!isPageValid) {
													dispatch(validate());
													return;
												} else {
													handlePrint();
												}
											}}
										>
											Submit Form
										</Button>
									</Space>
								</div>
							</Footer>
						</>
					)}
				</Layout>
			</div>
		);
	}
);

export default Renderer;
